/**
 * Build-time registry of extension admin pages (HUB-103).
 *
 * Every `packages/hub/extensions/<name>/admin/index.tsx` is discovered by a
 * Vite glob and code-split: the chunk is fetched only when that extension is
 * reported enabled by the hub (GET /extensions) and its page or nav is needed.
 * Keys are the extension FOLDER name, which HUB-099 pins equal to
 * `extension.name`.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

export interface ExtensionNavEntry {
  label: string;
  /** Path under `/admin/ext/<name>` — `''` for the page root. */
  path: string;
}

/** What `extensions/<name>/admin/index.tsx` exports. */
export interface ExtensionAdminModule {
  default: ComponentType;
  nav?: ExtensionNavEntry[];
}

// From admin/src/extensions/ → ../../../ = packages/hub/
const GLOB = import.meta.glob<ExtensionAdminModule>('../../../extensions/*/admin/index.tsx');

/** `'../../../extensions/foo_bar/admin/index.tsx'` → `'foo_bar'`. */
export function nameFromGlobKey(file: string): string | undefined {
  return /\/extensions\/([^/]+)\/admin\/index\.tsx$/.exec(file)?.[1];
}

export const EXTENSION_ADMIN_LOADERS: Record<string, () => Promise<ExtensionAdminModule>> =
  Object.fromEntries(
    Object.entries(GLOB)
      .map(([file, load]) => [nameFromGlobKey(file), load] as const)
      .filter((pair): pair is readonly [string, () => Promise<ExtensionAdminModule>] => pair[0] !== undefined),
  );

export function hasExtensionAdmin(name: string): boolean {
  return name in EXTENSION_ADMIN_LOADERS;
}

const modules = new Map<string, Promise<ExtensionAdminModule>>();

/** Memoized: one promise per name, shared by the sidebar (nav) and the route. */
export function loadExtensionAdmin(name: string): Promise<ExtensionAdminModule> | undefined {
  const load = EXTENSION_ADMIN_LOADERS[name];
  if (!load) return undefined;
  let pending = modules.get(name);
  if (!pending) {
    pending = load();
    modules.set(name, pending);
  }
  return pending;
}

const lazies = new Map<string, LazyExoticComponent<ComponentType>>();

/** Memoized `React.lazy` per name — a fresh `lazy()` each render would remount. */
export function lazyExtensionAdmin(name: string): LazyExoticComponent<ComponentType> | undefined {
  const pending = loadExtensionAdmin(name);
  if (!pending) return undefined;
  let component = lazies.get(name);
  if (!component) {
    component = lazy(() => pending.then((mod) => ({ default: mod.default })));
    lazies.set(name, component);
  }
  return component;
}

/** `/admin/ext/<name>` or `/admin/ext/<name>/<path>`. */
export function extensionAdminRoute(name: string, path = ''): string {
  const trimmed = path.replace(/^\/+/, '');
  return trimmed ? `/admin/ext/${name}/${trimmed}` : `/admin/ext/${name}`;
}
